/**
 * Category Revenue Chart Component
 *
 * Displays a stacked bar chart showing revenue by category
 * with customer breakdown showing each customer's contribution
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import type { CategoryRevenue } from "@/shared/types/sales-analytics";

interface CategoryRevenueChartProps {
  data: CategoryRevenue[];
  title?: string;
  description?: string;
}

const CUSTOMER_COLORS = [
  "#3b82f6", // blue-500
  "#10b981", // emerald-500
  "#f59e0b", // amber-500
  "#ef4444", // red-500
  "#8b5cf6", // violet-500
  "#ec4899", // pink-500
  "#14b8a6", // teal-500
  "#f97316", // orange-500
  "#6366f1", // indigo-500
  "#a855f7", // purple-500
  "#06b6d4", // cyan-500
  "#84cc16", // lime-500
  "#f43f5e", // rose-500
  "#0ea5e9", // sky-500
  "#d946ef", // fuchsia-500
];

export function CategoryRevenueChart({
  data,
  title = "Revenue by Category",
  description = "Revenue breakdown by customer for each product category"
}: CategoryRevenueChartProps) {
  if (!data || data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground text-center py-8">No category data available</div>
        </CardContent>
      </Card>
    );
  }

  // Sort by revenue descending
  const sortedCategories = [...data].sort((a, b) => b.revenue - a.revenue);

  // Collect all unique customers across all categories
  const allCustomers = new Set<string>();
  sortedCategories.forEach(cat => {
    if (cat.customerBreakdown) {
      cat.customerBreakdown.forEach(customer => {
        allCustomers.add(customer.customerName);
      });
    }
  });

  const uniqueCustomers = Array.from(allCustomers);

  // Dynamic Y-axis formatter based on value magnitude
  const formatYAxis = (value: number) => {
    if (value >= 1000000) {
      return `฿${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `฿${(value / 1000).toFixed(0)}K`;
    }
    return `฿${value}`;
  };

  // Transform data for stacked bar chart
  // Each category becomes a row with customer names as keys
  const chartData = sortedCategories.map(cat => {
    const row: any = {
      categoryName: cat.categoryName,
      category: cat.category,
      totalRevenue: cat.revenue,
      projectCount: cat.projectCount,
      customerCount: cat.customerCount,
      commissionRate: cat.commissionRate
    };

    // Add each customer's revenue for this category
    if (cat.customerBreakdown) {
      cat.customerBreakdown.forEach(customer => {
        row[customer.customerName] = customer.revenue;
      });
    }

    return row;
  });

  // Custom tooltip showing customer breakdown
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || payload.length === 0) return null;

    const data = payload[0].payload;

    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg max-w-xs">
        <p className="font-semibold text-sm mb-2">{label}</p>
        <p className="text-sm text-gray-600 mb-2">
          Total: <span className="font-semibold text-blue-600">
            ฿{data.totalRevenue.toLocaleString()}
          </span>
        </p>

        {/* Customer breakdown */}
        <div className="border-t pt-2 mt-2 space-y-1">
          <p className="text-xs text-gray-500 mb-1">Customer Contributions:</p>
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: entry.color }}
                />
                <span className="text-gray-700">{entry.name}</span>
              </div>
              <span className="font-semibold">฿{entry.value.toLocaleString()}</span>
            </div>
          ))}
        </div>

        <div className="border-t pt-2 mt-2 text-xs text-gray-600 space-y-1">
          <p>Projects: <span className="font-semibold">{data.projectCount}</span></p>
          <p>Customers: <span className="font-semibold">{data.customerCount}</span></p>
          {data.commissionRate > 0 && (
            <p>Rate: <span className="font-semibold">{(data.commissionRate * 100).toFixed(1)}%</span></p>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={500}>
          <BarChart
            data={chartData}
            margin={{ top: 5, right: 30, left: 20, bottom: 50 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="categoryName"
              angle={-45}
              textAnchor="end"
              height={100}
              tick={{ fontSize: 12 }}
            />
            <YAxis
              tickFormatter={formatYAxis}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: '12px' }}
              iconType="rect"
              iconSize={10}
            />

            {/* Create a Bar for each unique customer */}
            {uniqueCustomers.map((customer, index) => (
              <Bar
                key={customer}
                dataKey={customer}
                stackId="a"
                fill={CUSTOMER_COLORS[index % CUSTOMER_COLORS.length]}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>

        {/* Summary */}
        <div className="mt-4 pt-4 border-t text-sm text-muted-foreground text-center">
          Total: ฿{chartData.reduce((sum, cat) => sum + cat.totalRevenue, 0).toLocaleString()}
          {' • '}
          {chartData.length} {chartData.length === 1 ? 'category' : 'categories'}
          {' • '}
          {uniqueCustomers.length} {uniqueCustomers.length === 1 ? 'customer' : 'customers'}
        </div>
      </CardContent>
    </Card>
  );
}
